import { PLAYER_STATS_SCOPE_TOP11 } from './constants'

const toNumber = value => {
  const parsed = Number.parseInt(value, 10)
  return Number.isNaN(parsed) ? 0 : parsed
}

const toPlayerArray = playerDetails => {
  if (Array.isArray(playerDetails)) {
    return playerDetails
  }

  if (Array.isArray(playerDetails?.players)) {
    return playerDetails.players
  }

  return []
}

const toPlayedMatchesArray = playedMatches => {
  if (Array.isArray(playedMatches)) {
    return playedMatches
  }

  if (playedMatches && typeof playedMatches === 'object') {
    return Object.values(playedMatches)
  }

  return []
}

const toUpcomingMatchesArray = upcomingMatches => {
  if (Array.isArray(upcomingMatches)) {
    return upcomingMatches
  }

  if (upcomingMatches && typeof upcomingMatches === 'object') {
    return Object.values(upcomingMatches)
  }

  return []
}

const toArray = value => {
  if (Array.isArray(value)) {
    return value
  }

  if (value === undefined || value === null) {
    return []
  }

  return [value]
}

const toFixed1 = value => {
  return Math.round(value * 10) / 10
}

const formatSignedInteger = value => {
  if (!Number.isInteger(value)) {
    return '-'
  }

  if (value > 0) {
    return `+${value}`
  }

  return `${value}`
}

const toInteger = value => {
  const parsed = Number.parseInt(value, 10)
  return Number.isInteger(parsed) ? parsed : null
}

const decodeHtmlEntities = value => {
  const label = `${value || ''}`

  return label
    .replace(/&amp;/gi, '&')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
}

const getBestYouthSalaryPlayer = team => {
  const players = toPlayerArray(team?.PlayerDetails)
  let bestPlayer = null

  players.forEach(player => {
    if (!player?.Promoted || typeof player.Promoted !== 'object') {
      return
    }

    const salary = toNumber(player?.Salary)

    if (!bestPlayer || salary > bestPlayer.salary) {
      bestPlayer = {
        playerId: toNumber(player?.PlayerID),
        salary,
      }
    }
  })

  return bestPlayer
}

const shortenNickname = value => {
  const label = `${value || ''}`

  if (label.length <= 15) {
    return label
  }

  return `${label.slice(0, 15)}...`
}

const getPowerRatingValue = team => {
  const femmePower = team?.FemmeTeam?.PowerRating

  if (typeof femmePower === 'number' || typeof femmePower === 'string') {
    return femmePower
  }

  if (femmePower && typeof femmePower === 'object') {
    if (typeof femmePower.PowerRating === 'number' || typeof femmePower.PowerRating === 'string') {
      return femmePower.PowerRating
    }
  }

  const primaryPower = team?.PrimaryTeam?.PowerRating

  if (typeof primaryPower === 'number' || typeof primaryPower === 'string') {
    return primaryPower
  }

  return '-'
}

const getPlayersForStats = (playerDetails, scope) => {
  const players = toPlayerArray(playerDetails)

  if (scope !== PLAYER_STATS_SCOPE_TOP11) {
    return players
  }

  return [...players]
    .sort((first, second) => toNumber(second?.Salary) - toNumber(first?.Salary))
    .slice(0, 11)
}

const getPlayerStats = (playerDetails, scope) => {
  const players = getPlayersForStats(playerDetails, scope)
  const playerCountForStats = players.length

  if (!players.length) {
    return {
      salaryTotal: '-',
      tsiTotal: '-',
      avgAge: '-',
      avgExperience: '-',
      avgStamina: '-',
      avgForm: '-',
      specialtyCount: 0,
      playerCountForStats,
    }
  }

  const totals = players.reduce(
    (accumulator, player) => {
      const age = Array.isArray(player?.Age) ? toNumber(player.Age[0]) : 0
      const ageDays = Array.isArray(player?.Age) ? toNumber(player.Age[1]) : 0

      accumulator.salary += toNumber(player?.Salary)
      accumulator.tsi += toNumber(player?.TSI)
      accumulator.ageYears += age + ageDays / 112
      accumulator.experience += toNumber(player?.Experience)
      accumulator.stamina += toNumber(player?.StaminaSkill)
      accumulator.form += toNumber(player?.PlayerForm)
      accumulator.specialtyCount += toNumber(player?.Specialty) > 0 ? 1 : 0
      return accumulator
    },
    {
      salary: 0,
      tsi: 0,
      ageYears: 0,
      experience: 0,
      stamina: 0,
      form: 0,
      specialtyCount: 0,
    }
  )

  const count = players.length

  return {
    salaryTotal: toFixed1(totals.salary / 10),
    tsiTotal: totals.tsi,
    avgAge: toFixed1(totals.ageYears / count),
    avgExperience: toFixed1(totals.experience / count),
    avgStamina: toFixed1(totals.stamina / count),
    avgForm: toFixed1(totals.form / count),
    specialtyCount: totals.specialtyCount,
    playerCountForStats,
  }
}

const getStoredPlayerStats = (team, scope) => {
  const storedStats = team?.AveragePlayerStats?.[scope === PLAYER_STATS_SCOPE_TOP11 ? 'Top11' : 'All']

  if (!storedStats) {
    return null
  }

  return {
    salaryTotal: storedStats.SalaryTotal ?? '-',
    tsiTotal: storedStats.TsiTotal ?? '-',
    avgAge: storedStats.AvgAge ?? '-',
    avgExperience: storedStats.AvgExperience ?? '-',
    avgStamina: storedStats.AvgStamina ?? '-',
    avgForm: storedStats.AvgForm ?? '-',
    specialtyCount: storedStats.SpecialtyCount ?? '-',
    playerCountForStats: storedStats.PlayerCount ?? '-',
  }
}

const getTrendStats = ({ currentValue, previousValue }) => {
  if (!Number.isFinite(currentValue) || !Number.isFinite(previousValue)) {
    return null
  }

  const roundedCurrent = toFixed1(currentValue)
  const roundedPrevious = toFixed1(previousValue)
  const delta = toFixed1(roundedCurrent - roundedPrevious)

  if (delta === 0) {
    return null
  }

  return delta
}

const getBestHatstats = playedMatches => {
  const matches = toPlayedMatchesArray(playedMatches)

  if (!matches.length) {
    return {
      value: 0,
      matchId: null,
    }
  }

  return matches.reduce((best, match) => {
    const hatstats =
      toNumber(match?.RatingMidfield) * 3 +
      toNumber(match?.RatingRightDef) +
      toNumber(match?.RatingMidDef) +
      toNumber(match?.RatingLeftDef) +
      toNumber(match?.RatingRightAtt) +
      toNumber(match?.RatingMidAtt) +
      toNumber(match?.RatingLeftAtt)

    const matchId = toInteger(match?.MatchID)

    if (hatstats > best.value) {
      return {
        value: hatstats,
        matchId,
      }
    }

    return best
  }, {
    value: 0,
    matchId: null,
  })
}

const getCompetitiveHatstatsHistory = playedMatches => {
  return toPlayedMatchesArray(playedMatches)
    .filter(match => [1, 2, 3].includes(Number.parseInt(match?.MatchTypeID ?? match?.MatchType, 10)))
    .map(match => {
      const hatstats =
        toNumber(match?.RatingMidfield) * 3 +
        toNumber(match?.RatingRightDef) +
        toNumber(match?.RatingMidDef) +
        toNumber(match?.RatingLeftDef) +
        toNumber(match?.RatingRightAtt) +
        toNumber(match?.RatingMidAtt) +
        toNumber(match?.RatingLeftAtt)

      return {
        matchId: toInteger(match?.MatchID),
        matchDate: match?.MatchDate || '',
        hatstats,
      }
    })
    .filter(match => Number.isFinite(match.hatstats) && match.matchDate)
    .sort((first, second) => `${first.matchDate}`.localeCompare(`${second.matchDate}`))
}

const getCupIcon = team => {
  const cupLevel = Number.parseInt(team?.FemmeTeam?.Cup?.CupLevel, 10)

  if (!Number.isInteger(cupLevel)) {
    return ''
  }

  if (cupLevel === 1) {
    return '/cupA.png'
  }

  if (cupLevel === 2) {
    const cupLevelIndex = Number.parseInt(team?.FemmeTeam?.Cup?.CupLevelIndex, 10)

    if (cupLevelIndex === 1) {
      return '/cupB1v1.png'
    }

    if (cupLevelIndex === 2) {
      return '/cupB2v1.png'
    }

    if (cupLevelIndex === 3) {
      return '/cupB3v1.png'
    }

    return ''
  }

  if (cupLevel === 3) {
    return '/cupC.png'
  }

  return ''
}

const getCupMatchUrl = team => {
  const upcomingMatches = toUpcomingMatchesArray(team?.UpcomingMatches)
  const cupMatch = upcomingMatches.find(match => {
    const status = `${match?.Status || ''}`.trim().toUpperCase()
    const matchType = Number.parseInt(match?.MatchTypeID ?? match?.MatchType, 10)
    return status === 'UPCOMING' && matchType === 3
  })

  const matchId = Number.parseInt(cupMatch?.MatchID, 10)
  if (!Number.isInteger(matchId)) {
    return ''
  }

  return `https://hattrick.org/goto.ashx?path=/Club/Matches/Match.aspx?matchID=${matchId}`
}

const resolveFlagCode = ({ countryCode, countryName }) => {
  const normalizedCode = `${countryCode || ''}`.trim().toLowerCase()
  const normalizedName = `${countryName || ''}`.trim().toLowerCase()

  if (normalizedCode !== 'gb' && normalizedCode !== 'uk') {
    return normalizedCode
  }

  if (normalizedName === 'england') {
    return 'gb-eng'
  }

  if (normalizedName === 'scotland') {
    return 'gb-sct'
  }

  if (normalizedName === 'wales' || normalizedName === 'cymru') {
    return 'gb-wls'
  }

  if (normalizedName === 'northern ireland') {
    return 'gb-nir'
  }

  return normalizedCode
}

export const mapTeamRow = (team, playerStatsScope) => {
  const playerStats = getStoredPlayerStats(team, playerStatsScope) || getPlayerStats(team?.PlayerDetails, playerStatsScope)
  const oldPlayerStats = getPlayerStats(team?.OldPlayerDetails, playerStatsScope)
  const bestYouthSalaryPlayer = getBestYouthSalaryPlayer(team)
  const youthTeamId = Number.parseInt(team?.FemmeTeam?.YouthTeamID, 10)
  const trainerId = Number.parseInt(team?.FemmeTeam?.Trainer?.TrainerId, 10)
  const hasYouthSchool = Number.isInteger(youthTeamId) && youthTeamId === 0
  const hasYouthTeam = Number.isInteger(youthTeamId) && youthTeamId > 0
  const currentCapacity = team?.FemmeTeam?.Arena?.CurrentCapacity || {}
  const arenaTerraces = toInteger(currentCapacity?.Terraces)
  const arenaBasic = toInteger(currentCapacity?.Basic)
  const arenaRoof = toInteger(currentCapacity?.Roof)
  const arenaVip = toInteger(currentCapacity?.VIP)
  const arenaTotal = toInteger(currentCapacity?.Total)
  const arenaId = toInteger(team?.Arena) || toInteger(team?.FemmeTeam?.Arena?.ArenaID)
  const leaguePosition = toInteger(team?.LeagueDetails?.Position)
  const leaguePoints = toInteger(team?.LeagueDetails?.Points)
  const goalsFor = toInteger(team?.LeagueDetails?.GoalsFor)
  const goalsAgainst = toInteger(team?.LeagueDetails?.GoalsAgainst)
  const goalDiff =
    Number.isInteger(goalsFor) && Number.isInteger(goalsAgainst)
      ? goalsFor - goalsAgainst
      : null
  const leagueTable = toArray(team?.LeagueDetails?.Table)
  const cutoffPosition = Number.isInteger(leaguePosition) && leaguePosition <= 4 ? 5 : 4
  const cutoffTableEntry = leagueTable.find(entry => toInteger(entry?.Position) === cutoffPosition)
  const cutoffPoints = toInteger(cutoffTableEntry?.Points)
  const pointsCutoffGap =
    Number.isInteger(leaguePosition) && Number.isInteger(leaguePoints) && Number.isInteger(cutoffPoints)
      ? formatSignedInteger(leaguePoints - cutoffPoints)
      : '-'
  const hasArenaBreakdown =
    Number.isInteger(arenaTerraces) ||
    Number.isInteger(arenaBasic) ||
    Number.isInteger(arenaRoof) ||
    Number.isInteger(arenaVip)
  const arenaTooltip = hasArenaBreakdown
    ? `Terraces: ${arenaTerraces ?? '-'} | Basic: ${arenaBasic ?? '-'} | Roof: ${arenaRoof ?? '-'} | VIP: ${arenaVip ?? '-'}`
    : ''
  const trainerType = Number.parseInt(team?.FemmeTeam?.Trainer?.TrainerType, 10)
  const coachTypeCode =
    trainerType === 0
      ? 'D'
      : trainerType === 1
        ? 'O'
        : trainerType === 2
          ? 'N'
          : '-'
  const coachTypeLabel =
    coachTypeCode === 'D'
      ? 'Defensive'
      : coachTypeCode === 'O'
        ? 'Offensive'
        : coachTypeCode === 'N'
          ? 'Neutral'
          : '-'
  const bestHatstats = team?.isAverageTeam
    ? { value: Number.isFinite(Number(team?.HatstatsMax)) ? Number(team.HatstatsMax) : 0, matchId: null }
    : getBestHatstats(team?.PlayedMatches)
  const avgStaminaDelta = getTrendStats({
    currentValue: playerStats.avgStamina,
    previousValue: oldPlayerStats.avgStamina,
  })
  const avgFormDelta = getTrendStats({
    currentValue: playerStats.avgForm,
    previousValue: oldPlayerStats.avgForm,
  })

  return {
    ...team,
    ...playerStats,
    avgStaminaDelta,
    avgFormDelta,
    specialtyCountDisplay: team?.isAverageTeam
      ? playerStats.specialtyCount
      : `${playerStats.specialtyCount}/${playerStats.playerCountForStats}`,
    youthTopSalary: bestYouthSalaryPlayer ? toFixed1(bestYouthSalaryPlayer.salary / 10) : null,
    youthTopSalaryUrl: bestYouthSalaryPlayer?.playerId
      ? `https://hattrick.org/goto.ashx?path=/Club/Players/Player.aspx?playerId=${bestYouthSalaryPlayer.playerId}`
      : '',
    youthDisplay: hasYouthTeam ? 'team' : hasYouthSchool ? 'school' : '',
    youthTeamModeLabel: hasYouthTeam ? 'Team' : hasYouthSchool ? 'Network' : '-',
    youthTeamModeSort: hasYouthTeam ? 2 : hasYouthSchool ? 1 : 0,
    youthTeamUrl: hasYouthTeam
      ? `https://hattrick.org/goto.ashx?path=/Club/Youth/?YouthTeamID=${youthTeamId}`
      : '',
    cupIcon: getCupIcon(team),
    cupMatchUrl: getCupMatchUrl(team),
    coach:
      Number.isInteger(Number.parseInt(team?.FemmeTeam?.Trainer?.TrainerSkillLevel, 10)) &&
      Number.isInteger(Number.parseInt(team?.FemmeTeam?.Trainer?.Leadership, 10))
        ? `${Number.parseInt(team.FemmeTeam.Trainer.TrainerSkillLevel, 10)}/${Number.parseInt(team.FemmeTeam.Trainer.Leadership, 10)}`
        : '-',
    coachTypeCode,
    type: coachTypeLabel,
    coachUrl:
      Number.isInteger(Number.parseInt(team?.TeamID, 10)) && Number.isInteger(trainerId)
        ? `https://hattrick.org/goto.ashx?path=/Club/Specialists/Trainer.aspx?teamId=${Number.parseInt(team.TeamID, 10)}&trainerId=${trainerId}`
        : '',
    userLabel: team?.UserFlag || 'Manager',
    userShortLabel: shortenNickname(team?.UserFlag || 'Manager'),
    userFlagCode: resolveFlagCode({
      countryCode: team?.PrimaryCountryCode || team?.FemmeCountryCode,
      countryName: team?.PrimaryCountryName || team?.FemmeCountryName,
    }),
    userUrl: team?.UserID
      ? `https://hattrick.org/goto.ashx?path=/Club/Manager/?userId=${team.UserID}`
      : '',
    userCountryUrl:
      team?.UserLeagueID || team?.PrimaryTeam?.LeagueID || team?.PrimaryTeam?.Nation
        ? `https://hattrick.org/goto.ashx?path=/World/Leagues/League.aspx?LeagueID=${
            team?.UserLeagueID || team?.PrimaryTeam?.LeagueID || team?.PrimaryTeam?.Nation
          }`
        : '',
    teamUrl: team?.TeamID
      ? `https://hattrick.org/goto.ashx?path=/Club/?TeamID=${team.TeamID}`
      : '',
    countryLeagueUrl: team?.FemmeNationID || team?.FemmeTeam?.League?.LeagueID
      ? `https://hattrick.org/goto.ashx?path=/World/Leagues/League.aspx?LeagueID=${team?.FemmeNationID || team?.FemmeTeam?.League?.LeagueID}`
      : '',
    flagCode: resolveFlagCode({
      countryCode: team?.FemmeFlag || team?.FemmeCountryCode,
      countryName: team?.FemmeCountryName,
    }),
    teamName: decodeHtmlEntities(team?.FemmeTeam?.TeamName || team?.PrimaryTeam?.TeamName || '-'),
    cupSeed: team?.CupSeed ?? '-',
    inheritedCupSeed: team?.InheritedCupSeed ?? '-',
    remainingCupSeed: team?.RemainingCupSeed ?? '-',
    cupUrl: team?.TeamID
      ? `https://hattrick.org/goto.ashx?path=/Club/Cup/?teamId=${team.TeamID}`
      : '',
    fanClub: team?.FemmeTeam?.Fanclub?.FanclubSize ?? '-',
    hatstats: bestHatstats.value,
    hatstatsHistory: team?.isAverageTeam ? [] : getCompetitiveHatstatsHistory(team?.PlayedMatches),
    hatstatsMatchUrl: bestHatstats.matchId
      ? `https://hattrick.org/goto.ashx?path=/Club/Matches/Match.aspx?matchID=${bestHatstats.matchId}`
      : '',
    powerRating: getPowerRatingValue(team),
    rank:
      team?.FemmeTeam?.TeamRank ||
      team?.PrimaryTeam?.TeamRank ||
      team?.FemmeTeam?.LeagueLevelUnit?.Rank ||
      '-',
    leaguePosition: leaguePosition === null ? '-' : leaguePosition,
    points: leaguePoints === null ? (team?.FemmeTeam?.Points || '-') : leaguePoints,
    pointsCutoffGap,
    arenaCapacity: Number.isInteger(arenaTotal) ? arenaTotal : '-',
    arenaUrl: arenaId
      ? `https://hattrick.org/goto.ashx?path=/Club/Stadium/?stadiumId=${arenaId}`
      : '',
    arenaCapacityTooltip: arenaTooltip,
    goalDiff: goalDiff === null ? '-' : goalDiff,
    series: team?.FemmeTeam?.LeagueLevelUnit?.LeagueLevelUnitName || '-',
    seriesUrl: toInteger(team?.FemmeTeam?.LeagueLevelUnit?.LeagueLevelUnitID)
      || toInteger(team?.LeagueDetails?.LeagueLevelUnitID)
      ? `https://hattrick.org/goto.ashx?path=/World/Series/?LeagueLevelUnitID=${
        toInteger(team?.FemmeTeam?.LeagueLevelUnit?.LeagueLevelUnitID)
        || toInteger(team?.LeagueDetails?.LeagueLevelUnitID)
      }`
      : '',
  }
}
